import { Star } from "lucide-react";
import { motion } from "framer-motion";
import avatarAmarilda from "@/assets/testimonial-amarilda.jpg";
import avatarJulio from "@/assets/testimonial-julio.jpg";
import avatarYeidi from "@/assets/testimonial-yeidi.jpg";
import { useTranslation } from "react-i18next";

const testimonials = [
  {
    id: 1,
    name: "Amarilda Natalia",
    role: "Hóspede",
    avatar: avatarAmarilda,
    rating: 5,
    text: "Reservei um T3 no Talatona em 5 minutos. Fiz check-in sem stress nenhum. A experiência foi impecável do início ao fim.",
    accent: "from-primary/20 via-bera-gold/10 to-primary/5",
    glow: "group-hover:shadow-primary/10",
  },
  {
    id: 2,
    name: "Júlio Santos",
    role: "Proprietário",
    avatar: avatarJulio,
    rating: 5,
    text: "Tenho 3 imóveis listados. Só este mês recebi 12 reservas — a ocupação subiu 40%. A plataforma mudou o meu negócio.",
    accent: "from-bera-gold/20 via-primary/10 to-bera-gold/5",
    glow: "group-hover:shadow-bera-gold/10",
  },
  {
    id: 3,
    name: "Yeidi Garcia",
    role: "Afiliada",
    avatar: avatarYeidi,
    rating: 5,
    text: "Partilho links no meu Instagram e já acumulei mais de 85.000 Kz em comissões. Recomendo a todos!",
    accent: "from-primary/15 via-bera-gold/15 to-primary/5",
    glow: "group-hover:shadow-primary/10",
  },
];

const GlowingStar = ({ delay }: { delay: number }) => (
  <motion.div
    className="relative"
    initial={{ scale: 0.8, opacity: 0 }}
    whileInView={{ scale: 1, opacity: 1 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.3, type: "spring", stiffness: 200 }}
  >
    <Star className="w-[18px] h-[18px] fill-primary text-primary drop-shadow-[0_0_6px_hsl(var(--primary)/0.6)]" strokeWidth={0.5} />
    <motion.div
      className="absolute inset-0 rounded-full"
      animate={{ opacity: [0.4, 0.8, 0.4] }}
      transition={{ duration: 2, repeat: Infinity, delay: delay * 0.5 }}
      style={{ boxShadow: "0 0 8px 2px hsl(var(--primary) / 0.35)" }}
    />
  </motion.div>
);

const Testimonials = () => {
  const { t } = useTranslation();
  return (
    <section className="relative py-8 md:py-12 overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-muted/40 via-background to-muted/30" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/[0.04] rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-bera-gold/[0.04] rounded-full blur-3xl" />

      <div className="container relative mx-auto px-4">
        {/* Header */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.span
            className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-4 px-4 py-1.5 rounded-full bg-primary/[0.08] border border-primary/10"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            {t('testimonials.badge') || "Testemunhos"}
          </motion.span>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-3">
            {t('testimonials.title1') || "Quem usa, "} <span className="text-gradient">{t('testimonials.title2') || "recomenda"}</span>
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto text-sm md:text-base leading-relaxed">
            {t('testimonials.subtitle') || "Resultados reais de quem já reserva, anuncia e ganha com a BeraStays."}
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 max-w-5xl mx-auto">
          {testimonials.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className="group"
            >
              <div
                className={`relative h-full rounded-[1.25rem] bg-card/80 backdrop-blur-sm border border-border/50 p-5 md:p-6 shadow-md hover:shadow-2xl ${item.glow} hover:-translate-y-1.5 transition-all duration-400`}
              >
                {/* Top gradient accent */}
                <div className={`absolute inset-x-0 top-0 h-1 rounded-t-[1.25rem] bg-gradient-to-r ${item.accent} opacity-70 group-hover:opacity-100 transition-opacity duration-500`} />

                {/* Glowing stars */}
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: item.rating }).map((_, j) => (
                    <GlowingStar key={j} delay={i * 0.15 + j * 0.06} />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-foreground/85 leading-[1.65] text-[14px] mb-5 min-h-[60px] italic">
                  "{t(`testimonials.items.${i}.text`) || item.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3.5 pt-5 border-t border-border/30">
                  <motion.div
                    className="relative"
                    whileHover={{ scale: 1.08 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="absolute -inset-[2px] rounded-full bg-gradient-to-br from-primary/50 via-bera-gold/40 to-primary/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-[1px]" />
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="relative w-12 h-12 rounded-full object-cover ring-2 ring-border/40 group-hover:ring-primary/30 transition-all duration-300"
                    />
                    <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-gradient-to-br from-primary to-bera-gold border-2 border-card shadow-sm" />
                  </motion.div>
                  <div>
                    <p className="font-semibold text-foreground text-sm tracking-tight">
                      {item.name}
                    </p>
                    <p className="text-xs text-primary/70 font-medium mt-0.5">
                      {t(`testimonials.items.${i}.role`) || item.role}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
